/* THE LOCAL PHOTO KEY GRAMMAR — one definition, no dependencies.
 *
 * PHASE 4, STEP 7, ACT 2. These three builders were defined in
 * `photos.js` and are still exported from it unchanged — nothing that
 * imported them has to move. They live here because `photos.js` pulls in
 * `catalog.js`, `community.js`, `render.js` and `state.js`, and
 * `catalog.js` FETCHES `/catalog.json` at module load.
 *
 * ⚠ THAT IS NOT A TIDINESS ARGUMENT. The migration needs exactly these
 * three strings and nothing else in the app, and a gate that imports it
 * outside a browser was crashing on a catalog fetch before it reached a
 * single assertion. A module whose job is to name three keys should not
 * be reachable only through the note catalog.
 *
 * ── WHAT A NOTE PHOTO IS, AS OF PHASE 4 STEP 6 ─────────────────────
 *   notevault:photo:<id>:<slot>      the PREVIEW — a data URL. Always.
 *   notevault:photoraw:<id>:<slot>   the ORIGINAL FILE. Step 6 onward.
 *   notevault:photospec:<id>:<slot>  the transform {angle,crop}. Step 6.
 *
 * ⚠ A PHOTO ADDED BEFORE STEP 6 HAS NO SIDECAR. The preview is then the
 * only copy of that photograph that exists anywhere, which is what makes
 * the pre-Step-6 case the one Act 2 has to be explicit about rather than
 * quietly fall back into.
 */

export const PHOTO_KEY = (id, slot) => 'notevault:photo:' + id + ':' + slot
export const RAW_KEY = (id, slot) => 'notevault:photoraw:' + id + ':' + slot
export const SPEC_KEY = (id, slot) => 'notevault:photospec:' + id + ':' + slot
/* ── THE FOURTH KEY — PHASE 6.5 STEP 1, 2026-09-13 ──────────────────
 *   notevault:photohash:<id>:<slot>  SHA-256, hex, of the bytes the
 *                                    upload will SEND for this slot — the
 *                                    original when the sidecar was kept,
 *                                    else the preview. The same digest the
 *                                    endpoint computes over the body.
 *
 * ⚠ WHY A PHOTOGRAPH NEEDS TO KNOW ITS OWN HASH. Instant upload lets two
 * uploads of one slot be in flight when the collector replaces a photo
 * before the first lands; the endpoint's ref PATCH is unconditional, so
 * the OLDER one can land last and leave the row pointing at the previous
 * photograph. While the tab lives the client heals that from memory. If
 * the tab dies in the window, memory is gone and the row says `r2:` — and
 * the boot sweep selects on `idb:` only. This key is what lets the next
 * boot see that the row's `r2:` hash is not the hash of the bytes on the
 * device, and upload again.
 *
 * ⚠⚠ AN ABSENT HASH MEANS NOTHING. Every photograph taken before this key
 * existed has none, and none of them may be re-uploaded because of it. A
 * slot is stale only when a hash EXISTS and differs. */
export const HASH_KEY = (id, slot) => 'notevault:photohash:' + id + ':' + slot

/* ── THE PENDING BRAND IMAGE — PHASE 5, THE CLIENT HALF, 2026-09-07 ──
 * Three keys per slot, the same shape as a note's sidecar above, keyed by
 * the collector's USER ID rather than their username: a username can be
 * changed and the row the ref is written on cannot. They exist only
 * between "Use photo" and the upload landing — `uploadBrand` deletes all
 * three the moment the row carries an `r2:` ref — so a key that is
 * present means exactly one thing: this image has not reached the row
 * yet, and the local preview is what the screen shows meanwhile.
 * ⚠ `remote.js` routes every `notevault:brand*` key to LOCAL IndexedDB;
 * nothing here is ever read by another collector. */
export const BRAND_PREVIEW_KEY = (uid, slot) => 'notevault:brandprev:' + uid + ':' + slot
export const BRAND_RAW_KEY = (uid, slot) => 'notevault:brandraw:' + uid + ':' + slot
export const BRAND_META_KEY = (uid, slot) => 'notevault:brandmeta:' + uid + ':' + slot
